// 261010 Epic 2 / P4b-2 — logic thuần của ba tab bước 2 Tài sản (Nhân vật · Bối cảnh · Đạo cụ). Không antd ⇒ jest chạy được.
// Phạm vi dữ liệu: Nhân vật = THEO VIDEO đang mở; Bối cảnh + Đạo cụ = CẤP TẬP (plan) ⇒ đổi video không nạp lại, không đổi nội dung.
import type { EventRefBank, FilmEvent } from '../../types/film';
import type { LocationNeedsResponse } from '../../types/filmLocationAssets';
import type { BankPrep } from './bankPrepUtils';

export type AssetTabKey = 'CHARACTERS' | 'LOCATIONS' | 'PROPS';
export const ASSET_TABS: AssetTabKey[] = ['CHARACTERS', 'LOCATIONS', 'PROPS'];
export const DEFAULT_ASSET_TAB: AssetTabKey = 'LOCATIONS';
const TAB_NAME: Record<AssetTabKey, string> = { CHARACTERS: 'Nhân vật', LOCATIONS: 'Bối cảnh', PROPS: 'Đạo cụ' };

export type AssetTabAction = { type: 'SELECT'; tab: AssetTabKey } | { type: 'PLAN_CHANGED' };
/** Tab đang chọn KHÔNG phụ thuộc video (không có action "đổi video") ⇒ đổi video giữ tab. Đổi plan ⇒ về mặc định. */
export function assetTabReducer(state: AssetTabKey, action: AssetTabAction): AssetTabKey {
  if (action.type === 'PLAN_CHANGED') return DEFAULT_ASSET_TAB;
  return ASSET_TABS.includes(action.tab) ? action.tab : state;
}

/** Số trên tab: Nhân vật = số nhân vật của video; Bối cảnh = số location của plan (null khi chưa nạp ⇒ không hiện số); Đạo cụ = số props. P8e: `prep` tuỳ chọn — nhân vật/đạo cụ lấy `total` của prep; bối cảnh giữ `locations`. */
export function assetTabLabels(c: { characters: number; locations: number | null; props: number; prep?: BankPrep | null }): Array<{ key: AssetTabKey; name: string; count: number | null }> {
  const fromPrep = (key: 'chars' | 'props', fallback: number): number | null => {
    if (c.prep === undefined) return fallback;
    if (c.prep === null) return null;
    return c.prep.groups.find((g) => g.key === key)?.total ?? null;
  };
  const n: Record<AssetTabKey, number | null> = {
    CHARACTERS: fromPrep('chars', c.characters),
    LOCATIONS: c.locations,
    PROPS: fromPrep('props', c.props),
  };
  return ASSET_TABS.map((key) => ({ key, name: TAB_NAME[key], count: n[key] }));
}

/**
 * Nạp `location-needs` theo `planId` — KHÔNG nhận `eventId`: đổi video không nạp lại. Cùng planId gọi lại ⇒ dùng kết quả đang có; lỗi không cache; `reload` ép nạp lại.
 * (Thẻ location tự nạp `listLocationViews` riêng — nguồn trạng thái ảnh chuẩn / hướng tươi hơn `location-needs`.)
 */
export function createNeedsLoader(fetchNeeds: (planId: string) => Promise<LocationNeedsResponse>, fetchFirst?: (planId: string) => Promise<LocationNeedsResponse>) {
  const cache = new Map<string, Promise<LocationNeedsResponse>>();
  // `fetchFirst` (261029 P3 F1): CHỈ lần `load` đầu của loader này lấy từ chỗ giao một lần (đọc sớm); `reload` và mọi lần sau luôn `fetchNeeds` (gọi mới).
  let firstPending = !!fetchFirst;
  const run = (planId: string, first = false): Promise<LocationNeedsResponse> => {
    const p = first && fetchFirst ? fetchFirst(planId) : fetchNeeds(planId);
    cache.set(planId, p);
    p.catch(() => { if (cache.get(planId) === p) cache.delete(planId); });
    return p;
  };
  return {
    load: (planId: string): Promise<LocationNeedsResponse> => { const hit = cache.get(planId); if (hit) return hit; const first = firstPending; firstPending = false; return run(planId, first); },
    reload: (planId: string): Promise<LocationNeedsResponse> => run(planId),
  };
}

// ── tab Đạo cụ (chỉ đọc) ──
export const PROPS_EMPTY_TEXT = 'Plan này chưa có đạo cụ trong Bank.';
export interface PropRow { id: string; name: string; previewUrl: string | null; hasSheet: boolean; sheetLabel: 'có sheet' | 'chưa có sheet' }
export function propRowsOf(bank: Pick<EventRefBank, 'props'> | null | undefined): PropRow[] {
  return (bank?.props ?? []).map((p) => ({
    id: p.id, name: p.name, previewUrl: p.previewUrl || null, hasSheet: !!p.hasSheet, sheetLabel: p.hasSheet ? 'có sheet' : 'chưa có sheet',
  }));
}

// ── tab Nhân vật: chuyển NGUYÊN VĂN logic của case ASSETS cũ (không đổi luật) ──
export interface CharacterRow { name: string; status: 'có ảnh người mặc' | 'thiếu ảnh người mặc' | 'chưa có trong Bank'; imageUrl: string | null }
export function characterRowsOf(ev: Pick<FilmEvent, 'shots'> | null | undefined, bank: Pick<EventRefBank, 'characters'> | null | undefined): CharacterRow[] {
  const names = Array.from(new Set((ev?.shots || []).flatMap((s) => s.characterNames || [])));
  return names.map((n) => {
    const b = bank?.characters?.find((c) => c.name.toLowerCase() === n.toLowerCase());
    if (!b) return { name: n, status: 'chưa có trong Bank', imageUrl: null };
    return { name: n, status: b.hasWornLook ? 'có ảnh người mặc' : 'thiếu ảnh người mặc', imageUrl: b.wornLookPreviewUrl || b.previewUrl || null };
  });
}
